import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryClassProps {
  children: ReactNode;
  title: string;
  description: string;
  retryLabel: string;
}

interface ErrorBoundaryClassState {
  hasError: boolean;
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryClassProps,
  ErrorBoundaryClassState
> {
  state: ErrorBoundaryClassState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryClassState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title={this.props.title}
          subTitle={this.props.description}
          extra={
            <Button type="primary" onClick={this.handleRetry}>
              {this.props.retryLabel}
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <ErrorBoundaryClass
      title={t('home.documents.errorBoundary.title')}
      description={t('home.documents.errorBoundary.description')}
      retryLabel={t('home.documents.errorBoundary.retry')}
    >
      {children}
    </ErrorBoundaryClass>
  );
}
